import { debounce } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import useIntersectionObserver from '../../utils/intersection-observer-hook';
import { fetchGauntlets } from './gauntlet-api';
import { Gauntlet } from './GauntletCard';

export const useGauntlet = () => {
  const [gauntlets, setGauntlets] = useState<Gauntlet[]>([]);
  const [lastItem, setLastItem] = useState<string | null>(null);
  const [term, setTerm] = useState<string | null>(null);

  const endElRef = useRef(null);
  const contentRef = useRef(null);

  const entry = useIntersectionObserver(endElRef, {
    root: window.outerWidth < 768 ? null : contentRef.current,
    rootMargin: '50px',
    threshold: 1
  });

  const debouncedSearch = useCallback(
    debounce(async (query?: string) => {
      if (query && query.length < 3) return;
      setLastItem(null);
      setTerm(query || null);
      const data = await fetchGauntlets(query);
      setGauntlets(data.Items);
      setLastItem(data?.LastEvaluatedKey?.id);
    }, 1000),
    [lastItem]
  );

  const debouncedGetMoreResults = useCallback(
    debounce(async (query?: string, lastKey?: string) => {
      if (!lastKey && !lastItem) return;
      const data = await fetchGauntlets(query || term, lastKey || lastItem);
      setGauntlets(state => [...state, ...data.Items]);
      setLastItem(data?.LastEvaluatedKey?.id);
    }, 1000),
    [term, lastItem]
  );

  const search = (event: any) => {
    debouncedSearch(event.target.value);
  };

  useEffect(() => {
    fetchGauntlets().then(data => {
      setGauntlets(state => [...state, ...data.Items]);
      setLastItem(data.LastEvaluatedKey.id);
    });
  }, []);

  useEffect(() => {
    if (entry?.isIntersecting) {
      debouncedGetMoreResults();
    }
  }, [entry?.isIntersecting]);

  return { gauntlets, elements: { endElRef, contentRef }, search };
};
